import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  Wallet, CreditCard, FileText, TrendingUp, Calendar, 
  Download, Settings, Plus, AlertTriangle, CheckCircle,
  Activity, BarChart3, Clock, DollarSign, Brain, Target
} from 'lucide-react';
import { CreditTopUp } from '@/components/billing/CreditTopUp';
import { useWorkspaceCredits } from '@/hooks/useWorkspaceCredits';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { formatDistanceToNow } from 'date-fns';
import { CreditAnalyticsDashboard } from '@/components/credits/CreditAnalyticsDashboard';
import { SmartAlertsPanel } from '@/components/credits/SmartAlertsPanel';
import { ConversionOpportunities } from '@/components/credits/ConversionOpportunities';
import { PredictiveInsights } from '@/components/credits/PredictiveInsights';

export default function WorkspaceBillingAdvanced() {
  const { currentWorkspace } = useWorkspace();
  const [activeTab, setActiveTab] = useState('overview');
  const [showTopUp, setShowTopUp] = useState(false);
  
  const { 
    creditWallet, 
    transactions, 
    isLoadingWallet, 
    getBalanceStatus,
    estimateUsageRemaining 
  } = useWorkspaceCredits();

  // Check for topup action in URL
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'topup') {
      setShowTopUp(true);
    }
  }, []);

  const balanceStatus = getBalanceStatus();

  if (showTopUp) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <Button variant="outline" onClick={() => setShowTopUp(false)}>
            ← Back to Billing
          </Button>
        </div>
        <CreditTopUp />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Credit Management</h1>
          <p className="text-muted-foreground">Manage your credits and monitor usage</p>
        </div>
        <div className="flex items-center space-x-3">
          {balanceStatus.urgent && (
            <Badge className={balanceStatus.color}>
              <AlertTriangle className="h-3 w-3 mr-1" />
              {balanceStatus.status}
            </Badge>
          )}
          <Button onClick={() => setShowTopUp(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Top Up Credits
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3 className="h-4 w-4 mr-1" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="insights">
            <Brain className="h-4 w-4 mr-1" />
            AI Insights
          </TabsTrigger>
          <TabsTrigger value="opportunities">
            <Target className="h-4 w-4 mr-1" />
            Growth
          </TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Credit Balance Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Credit Balance</CardTitle>
                <Wallet className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoadingWallet ? (
                  <div className="h-8 bg-muted animate-pulse rounded"></div>
                ) : (
                  <div className="text-2xl font-bold text-foreground">
                    {Math.floor(creditWallet?.credits_balance || 0)}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Available credits
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Bills Remaining</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  ~{estimateUsageRemaining(1)}
                </div>
                <p className="text-xs text-muted-foreground">
                  At 1 credit per bill
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">This Month</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  {transactions?.filter(t => 
                    new Date(t.created_at) >= startOfMonth(new Date()) && 
                    t.transaction_type === 'usage_debit'
                  ).reduce((sum, t) => sum + Math.abs(t.amount), 0) || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  Credits consumed
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Status</CardTitle>
                {balanceStatus.status === 'Good' ? (
                  <CheckCircle className="h-4 w-4 text-success" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-warning" />
                )}
              </CardHeader>
              <CardContent>
                <Badge className={balanceStatus.color}>
                  {balanceStatus.status}
                </Badge>
                <p className="text-xs text-muted-foreground mt-1">
                  Account status
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Transaction History Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
              <p className="text-muted-foreground">Latest credit activity</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {transactions?.slice(0, 5).map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${
                        transaction.amount > 0 ? 'bg-success' : 'bg-destructive'
                      }`} />
                      <div>
                        <p className="text-sm font-medium">
                          {transaction.description || transaction.transaction_type.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(transaction.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${
                        transaction.amount > 0 ? 'text-success' : 'text-destructive'
                      }`}>
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount} credits
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Balance: {transaction.balance_after}
                      </p>
                    </div>
                  </div>
                ))}
                {(!transactions || transactions.length === 0) && (
                  <div className="text-center py-6 text-muted-foreground">
                    No transactions yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Smart Alerts */}
          <SmartAlertsPanel />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <CreditAnalyticsDashboard />
        </TabsContent>

        <TabsContent value="insights" className="space-y-6">
          <PredictiveInsights />
        </TabsContent>

        <TabsContent value="opportunities" className="space-y-6">
          <ConversionOpportunities />
        </TabsContent>

        <TabsContent value="transactions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>All Transactions</CardTitle>
              <p className="text-muted-foreground">Complete transaction history</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {transactions?.map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${
                        transaction.amount > 0 ? 'bg-success' : 'bg-destructive'
                      }`} />
                      <div>
                        <p className="text-sm font-medium">
                          {transaction.description || transaction.transaction_type.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(transaction.created_at), 'MMM dd, yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${
                        transaction.amount > 0 ? 'text-success' : 'text-destructive'
                      }`}>
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount} credits
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Balance: {transaction.balance_after}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Credit Settings</CardTitle>
              <p className="text-muted-foreground">Configure credit management preferences</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Auto Top-up</p>
                    <p className="text-sm text-muted-foreground">
                      Automatically purchase credits when balance is low
                    </p>
                  </div>
                  <Badge variant="outline">
                    {creditWallet?.auto_topup_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                
                <Button variant="outline" size="sm">
                  <Settings className="h-4 w-4 mr-2" />
                  Configure Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}